import { XR, createXRStore } from '@react-three/xr';
import { useEffect, useState, type PropsWithChildren } from 'react';

/**
 * WebXR wrapper. Mounted automatically inside <Canvas>; in 2D it is a
 * passthrough, in VR/AR it manages the immersive session.
 *
 * Both VR and AR sessions use the same store; call `xrStore.enterVR()`
 * or `xrStore.enterAR()` from a click handler.
 */
export const xrStore = createXRStore();

export function XRWrapper({ children }: PropsWithChildren) {
  return <XR store={xrStore}>{children}</XR>;
}

/**
 * "Enter VR" / "Enter AR" buttons — only rendered in browsers where
 * the matching WebXR session mode is actually supported, so the HUD
 * stays clean on regular desktops + phones and the affordance only
 * appears on headsets like Quest 3 (or PCs with a connected rig).
 */
export function XRButtons() {
  const [supported, setSupported] = useState<{ vr: boolean; ar: boolean }>({
    vr: false,
    ar: false,
  });

  useEffect(() => {
    const xr = (
      navigator as Navigator & {
        xr?: {
          isSessionSupported: (mode: string) => Promise<boolean>;
        };
      }
    ).xr;
    if (!xr) return;
    let cancelled = false;
    Promise.all([
      xr.isSessionSupported('immersive-vr').catch(() => false),
      xr.isSessionSupported('immersive-ar').catch(() => false),
    ]).then(([vr, ar]) => {
      if (!cancelled) setSupported({ vr, ar });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!supported.vr && !supported.ar) return null;

  return (
    <div className="xr-buttons">
      {supported.vr && (
        <button type="button" onClick={() => xrStore.enterVR()}>
          Enter VR
        </button>
      )}
      {supported.ar && (
        <button type="button" onClick={() => xrStore.enterAR()}>
          Enter AR
        </button>
      )}
    </div>
  );
}
